import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, ChevronLeft, ChevronRight, FileText, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 20;

const DocumentsReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0, total_pages: 1, page: 1 });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/sections/greige/documents-report', {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: appliedSearch || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents report');
      setData({ rows: [], total: 0, total_pages: 1, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const applySearch = (e) => {
    e?.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const clearFilters = () => {
    setSearch('');
    setAppliedSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/sections/greige/documents-report/export', {
        params: {
          search: appliedSearch || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `greige_documents_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const rows = data.rows || [];
  const total = data.total || 0;
  const totalPages = data.total_pages || 1;
  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);
  const hasFilters = appliedSearch || dateFrom || dateTo;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 pt-14 pb-6 sm:pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 pr-24 sm:pr-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} data-testid="docs-report-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-600 text-white"><FileText className="w-5 h-5" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Documents Report</h1>
              <p className="text-sm text-gray-500">Greige · All documents received — status completes when every bundle is put away in Greige Storage or shipped outward</p>
            </div>
          </div>
          <Button
            onClick={exportCsv}
            disabled={exporting || loading}
            className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="docs-report-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-5">
          <CardContent className="py-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <form onSubmit={applySearch} className="flex gap-2 flex-1">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search document no, vendor, vehicle, vendor doc no…"
                    className="pl-9"
                    data-testid="docs-report-search-input"
                  />
                </div>
                <Button type="submit" data-testid="docs-report-search-btn">Search</Button>
              </form>
              <div className="flex gap-2 items-end">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">From</label>
                  <Input type="date" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                    className="w-40" data-testid="docs-report-date-from" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">To</label>
                  <Input type="date" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                    className="w-40" data-testid="docs-report-date-to" />
                </div>
                {hasFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="text-gray-500" data-testid="docs-report-clear-btn">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table data-testid="docs-report-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Document Number</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Vehicle No</TableHead>
                  <TableHead className="text-right">Bundles Stored</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-500" data-testid="docs-report-empty">
                      No documents found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, idx) => (
                    <TableRow key={r.document_barcode} data-testid={`docs-report-row-${idx}`}>
                      <TableCell className="text-gray-400 text-sm">{startIdx + idx}</TableCell>
                      <TableCell className="text-gray-700" data-testid={`docs-report-date-${idx}`}>{r.date || '—'}</TableCell>
                      <TableCell className="font-mono font-medium text-gray-900" data-testid={`docs-report-doc-${idx}`}>{r.document_barcode}</TableCell>
                      <TableCell className="text-gray-700">{r.vendor_name || '—'}</TableCell>
                      <TableCell className="text-gray-600">{r.vehicle_no || '—'}</TableCell>
                      <TableCell className="text-right text-gray-600 text-sm">{r.stored_bundles}/{r.total_bundles}</TableCell>
                      <TableCell>
                        <Badge
                          className={r.status === 'Completed'
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}
                          data-testid={`docs-report-status-${idx}`}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500" data-testid="docs-report-count">
            {total === 0 ? 'No documents' : `Showing ${startIdx}–${endIdx} of ${total}`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="docs-report-prev-btn">
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <span className="text-sm text-gray-600" data-testid="docs-report-page">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="docs-report-next-btn">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsReport;
